import React, { useState, useEffect, useRef } from 'react';
import { Button } from 'react-bootstrap';
import { appMenuGroups, icons } from 'router/router';
import { useHistory, useLocation } from 'react-router';
import { useAppSelector } from 'app/hooks';
import { usePageStatus } from 'hooks/usePageStatus';
import { getUserId } from 'utils/sessionStorage';

const MiniListMenu = () => {
  const history = useHistory();
  const location = useLocation();
  const [showSubmenu, setShowSubmenu] = useState<string | null>(null);
  const submenuRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  const checkModule = useAppSelector((store) => store.basicInformation.checkModule);
  const roleFamily = useAppSelector((store) => store.family?.roleFamily);
  const roleTest = useAppSelector((store) => store.testing.roleTest);
  
  const studentId = getUserId();
  
  // Use the custom hook for page status
  const { pageStatus } = usePageStatus({ studentId: studentId || '' });


  // Handle click outside to close submenu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        submenuRef.current &&
        !submenuRef.current.contains(event.target as Node)
      ) {
        setShowSubmenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleIconClick = (route: any) => {
    if (route.isGroup === false) {
      history.push(route.route.path);
    } else {
      setShowSubmenu(showSubmenu === route.name ? null : route.name);
    }
  };

  const handleChildClick = (child: any) => {
    history.push(child?.path.replace(':id', studentId));
    setShowSubmenu(null);
  };

  const isActiveRoute = (route: any) => {
    const currentPath = location.pathname;
    
    if (route.isGroup === false) {
      const routePath = route.route.path.replace(':id', '');
      return currentPath === routePath || currentPath.startsWith(routePath);
    } else {
      return route.children.some((child: any) => {
        const childPath = child?.path.replace(':id', '');
        return currentPath === childPath || currentPath.startsWith(childPath);
      });
    }
  };

  const isChildActive = (child: any) => {
    return location.pathname.includes(child?.path.replace(':id', ''));
  };

  const filteredKeys_removeItem = (filteredKeys: string[], keyVal: string) => {
    var index = filteredKeys.findIndex((e: string) => e === keyVal);
    if (index > -1) {
      filteredKeys.splice(index, 1);
    }
  };

  const filteredKeys_checkRoleTest = (filteredKeys: string[], roleTest: any) => {
    if (roleTest) {
      if (roleTest?.satTest === false) {
        filteredKeys_removeItem(filteredKeys, '4.1');
      }
      if (roleTest?.actTest === false) {
        filteredKeys_removeItem(filteredKeys, '4.2');
      }
      if (roleTest?.actSubjectTest === false) {
        filteredKeys_removeItem(filteredKeys, '4.3');
      }
      if (roleTest?.ibSubjectTest === false) {
        filteredKeys_removeItem(filteredKeys, '4.4');
      }
      if (roleTest?.clepTest === false) {
        filteredKeys_removeItem(filteredKeys, '4.5');
      }
      if (roleTest?.toeflTest === false) {
        filteredKeys_removeItem(filteredKeys, '4.6');
      }
      if (roleTest?.ieltsAndPte === false) {
        filteredKeys_removeItem(filteredKeys, '4.7');
      }
      if (roleTest?.douLingoTest === false) {
        filteredKeys_removeItem(filteredKeys, '4.8');
      }
    }
  };

  const getCompletionStatus = (route: any) => {
    let module = '';
    switch (route?.name) {
      case 'Personal':
        module = '1.';
        break;
      case 'Family':
        module = '2.';
        break;
      case 'Education':
        module = '3.';
        break;
      case 'Tests':
        module = '4.';
        break;
      case 'Courses & Grades':
        module = '5.';
        break;
      case 'Activities & Honors':
        module = '6.';
        break;
      case 'Additional Info':
        module = '7.';
        break;
      // case 'GPA Forecaster':
      //   module = '9.';
      //   break;
      default:
        return null;
    }

    let filteredKeys = checkModule ? Object.keys(checkModule).filter((key) => key.startsWith(`${module}`)) : [];
    
    filteredKeys_checkRoleTest(filteredKeys, roleTest);

    if (!roleFamily?.siblings && filteredKeys.length > 0) {
      const index = filteredKeys.indexOf('2.4');
      if (index > -1) {
        filteredKeys.splice(index, 1);
      }
    }

    if (!roleFamily?.stepParent1 && !roleFamily?.stepParent2 && filteredKeys.length > 0) {
      const index = filteredKeys.indexOf('2.5');
      if (index > -1) {
        filteredKeys.splice(index, 1);
      }
    }

    const allTrue = filteredKeys.length > 0 && filteredKeys.every((key) => checkModule[key][0] === true);
    const allCounselorTrue = filteredKeys.length > 0 && filteredKeys.every((key) => checkModule[key][1] === true);

    return { allTrue, allCounselorTrue };
  };

  const renderSubmenu = (route: any) => {
    if (!showSubmenu || showSubmenu !== route.name) return null;

    return (
      <div
        ref={submenuRef}
        className="position-absolute bg-white border rounded shadow-sm"
        style={{
          left: '60px',
          top: '0',
          minWidth: '250px',
          zIndex: 1001,
          maxHeight: '400px',
          overflowY: 'auto'
        }}
      >
        <div className="p-2">
          <div className="fw-bold text-primary mb-2">{route.name}</div>
          {route.children.map((child: any, i: number) => {
            const disabled = child.permission && !{ ...roleFamily, ...roleTest }[child.permission];
            
            return disabled ? null : (
              <div
                key={i}
                className={`d-flex align-items-center p-2 cursor-pointer rounded mini-submenu-item ${
                  isChildActive(child) ? 'bg-primary text-white' : ''
                }`}
                onClick={() => handleChildClick(child)}
                style={{ cursor: 'pointer' }}
              >
                <span className="me-2">{child?.icon}</span>
                <span className="flex-grow-1">{child?.name}</span>
                
                {(() => {
                  // Check if this page is completed based on pageStatus
                  const currentStatus = pageStatus.find((status: any) => {
                    if (child?.name === 'Citizenship/Residency') {
                      return status.page_name === 'Citizenship-Residency';
                    } if(child?.name === 'General Info' && child?.path === '/application/general-info') {
                      return status.page_name === 'Test General Info';
                    }
                      else {
                      return status.page_name === child?.name;
                    }
                  });
                  
                  return currentStatus && (
                    <span className="ms-auto">
                      {currentStatus.student_completed && <img
                        alt="csu"
                        src="./assets/icons/tick-blue.svg"
                        style={{ marginTop: '-8px', marginLeft: '4px' }}
                      />}

                      {currentStatus.counselor_verified && <img
                        alt="counselor-approved"
                        src="./assets/icons/tick-green.svg"
                        style={{ marginTop: '-8px', marginLeft: '4px' }}
                      />}
                    </span>
                  );
                })()}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <>
      <div 
        ref={containerRef}
        className="h-100 d-flex flex-column bg-light border-end position-relative" 
        style={{ width: '60px' }}
      >
      <div className="flex-grow-1 d-flex flex-column py-2">
        {appMenuGroups.map((route: any, index: number) => {
          const IconComponent = icons[route.icon] || 'div';
          const isActive = isActiveRoute(route);

          return (
            <div key={index} className="position-relative">
              <Button
                variant="outline"
                className={`d-flex flex-column align-items-center justify-content-center mb-1 mx-1 border-0 position-relative mini-menu-item ${
                  isActive ? 'bg-primary text-white' : 'text-secondary'
                }`}
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '8px'
                }}
                onClick={() => handleIconClick(route)}
              >
                <IconComponent fontSize="small" />
                
                {/* Completion indicators */}
                {/* {status && (
                  <div className="position-absolute" style={{ top: '-2px', right: '-2px' }}>
                    {status.allTrue && (
                      <img
                        alt="completed"
                        src="./assets/icons/tick-blue.svg"
                        style={{ width: '12px', height: '12px' }}
                      />
                    )}
                    {status.allCounselorTrue && (
                      <img
                        alt="counselor-approved"
                        src="./assets/icons/tick-green.svg"
                        style={{ width: '12px', height: '12px', marginLeft: '2px' }}
                      />
                    )}
                  </div>
                )} */}
              </Button>
              {renderSubmenu(route)}
            </div>
          );
        })}
      </div>
    </div>
    <style>{`
      .mini-submenu-item:hover {
        background-color: #f8f9fa !important;
      }
    `}</style>
    </>
  );
};

export default MiniListMenu;
